import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Package, Heart } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProductCard from "@/components/ProductCard";

export const ORDER_LABELS = {
  pending: "In attesa di pagamento",
  confirmed: "Confermato (contrassegno)",
  paid: "Pagato",
  shipped: "Spedito",
  delivered: "Consegnato",
  cancelled: "Annullato",
};

export const ORDER_COLORS = {
  pending: "bg-[#ffd600] text-black",
  confirmed: "bg-[#ff4500] text-white",
  paid: "bg-[#10b981] text-white",
  shipped: "bg-[#062e20] text-white",
  delivered: "bg-[#0a0a0a] text-white",
  cancelled: "bg-destructive text-white",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [wishlist, setWishlist] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/wishlist").then((r) => setWishlist(r.data.items)).catch(() => {});
  }, [user]);

  if (user === false) return <Navigate to="/accedi?redirect=/account" replace />;
  if (user === null) return <div className="max-w-7xl mx-auto px-6 py-24 animate-pulse">Caricamento...</div>;

  return (
    <div data-testid="account-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="overline-tag">Il tuo spazio</div>
      <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl md:text-5xl">
        Ciao, {user.name.split(" ")[0]}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground font-mono">{user.email}</p>

      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="rounded-none">
          <TabsTrigger data-testid="tab-orders" value="orders" className="rounded-none uppercase text-xs font-bold tracking-wide">
            <Package className="w-4 h-4 mr-2" strokeWidth={1.5} /> Ordini
          </TabsTrigger>
          <TabsTrigger data-testid="tab-wishlist" value="wishlist" className="rounded-none uppercase text-xs font-bold tracking-wide">
            <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} /> Wishlist
          </TabsTrigger>
        </TabsList>

        <TabsContent value="orders">
          {orders === null ? (
            <div className="py-16 animate-pulse text-muted-foreground">Caricamento ordini...</div>
          ) : orders.length === 0 ? (
            <div data-testid="orders-empty" className="mt-8 border border-dashed border-[#e5e5e5] bg-white py-16 text-center">
              <p className="font-display text-2xl uppercase tracking-tight">Nessun ordine ancora</p>
              <Link to="/catalogo" className="btn-hard mt-6 inline-flex">Inizia lo shopping</Link>
            </div>
          ) : (
            <div className="mt-8 flex flex-col gap-5">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-card-${o.id}`} className="bg-white border border-[#e5e5e5] p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono text-xs text-muted-foreground">#{o.id.slice(0, 8)}</div>
                      <div className="text-sm mt-0.5">{new Date(o.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 ${ORDER_COLORS[o.status] || "bg-muted"}`}>
                        {ORDER_LABELS[o.status] || o.status}
                      </span>
                      <span className="font-display font-semibold text-lg text-[#ff4500]">{eur(o.total)}</span>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    {o.items.map((i) => (
                      <div key={i.product_id} className="flex items-center gap-3 text-sm">
                        <img src={i.image} alt="" className="w-10 h-10 object-cover border border-[#e5e5e5]" />
                        <span className="flex-1 text-muted-foreground">{i.name} × {i.quantity}</span>
                        <span className="font-mono">{eur(i.price * i.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="wishlist">
          {wishlist.length === 0 ? (
            <div data-testid="wishlist-empty" className="mt-8 border border-dashed border-[#e5e5e5] bg-white py-16 text-center">
              <p className="font-display text-2xl uppercase tracking-tight">Wishlist vuota</p>
              <p className="mt-2 text-sm text-muted-foreground">Tocca il cuore su un prodotto per salvarlo qui.</p>
            </div>
          ) : (
            <div data-testid="wishlist-grid" className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {wishlist.map((p, i) => (
                <ProductCard key={p.id} p={p} index={i} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
